import { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import AccordionTitle from './AccordionTitle';
import type { AccordionDataItem } from './types';

function initialOpenTabs(data: AccordionDataItem[]): number[] {
  // To always have the first accordion open initialize to [0] and remove first item from array
  const indexArr: number[] = [0];
  const newArr = [...data];
  newArr.shift();
  newArr.forEach((item, i) => {
    if (item.shouldOpenOnInitialLoad) {
      indexArr.push(i + 1);
    }
  });
  return indexArr;
}

type AccordionProps = {
  data: AccordionDataItem[];
  canRunInitialOpenTabs?: boolean;
  initializer?: () => number[];
  shouldScrollToTopOnChange?: boolean;
  canHaveMultipleOpen?: boolean;
  openIndexFromParent?: number;
  customTitle?: boolean;
  fullWidth?: boolean;
};

const Accordion = ({
  data,
  canRunInitialOpenTabs,
  initializer,
  shouldScrollToTopOnChange,
  canHaveMultipleOpen,
  openIndexFromParent,
  customTitle,
  fullWidth,
}: AccordionProps) => {
  const [hasRunInitialOpenTabs, setHasRunInitialOpenTabs] = useState<boolean>(false);
  const [openIndexes, setOpenIndexes] = useState<number[]>(() => (initializer ? initializer() : [0]));

  useEffect(() => {
    if (canRunInitialOpenTabs && !hasRunInitialOpenTabs) {
      setHasRunInitialOpenTabs(true);
      setOpenIndexes(initialOpenTabs(data));
    }
  }, [canRunInitialOpenTabs, data, hasRunInitialOpenTabs]);

  useEffect(() => {
    if (openIndexFromParent) {
      setOpenIndexes([openIndexFromParent]);
      window.scrollTo({ behavior: 'smooth', top: 0 });
    }
  }, [openIndexFromParent]);

  const onTitleClick = (index: number) => {
    if (canHaveMultipleOpen) {
      setOpenIndexes(openIndexes.includes(index) ? openIndexes.filter(i => i !== index) : [...openIndexes, index]);
    } else {
      setOpenIndexes(openIndexes.includes(index) ? [] : [index]);
    }

    shouldScrollToTopOnChange && window.scrollTo({ behavior: 'smooth', top: 0 });
  };
  return (
    <Box>
      {data.map(({ title, count, body }, i) => (
        <MuiAccordion
          key={`${i}+${title}`}
          expanded={openIndexes.includes(i)}
          onChange={() => onTitleClick(i)}
          sx={{
            mb: {
              xs: 'var(--spacing-md)',
              md: 'var(--spacing-xl)'
            },
            border: '1px solid #DBDDDC',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              mb: {
                xs: 'var(--spacing-md)',
                md: 'var(--spacing-xl)'
              }
            }
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <AccordionTitle
              onTitleClick={() => onTitleClick(i)}
              title={title}
              count={count}
              customTitle={customTitle}
              fullWidth={fullWidth}
            />
          </AccordionSummary>
          <AccordionDetails>{body}</AccordionDetails>
        </MuiAccordion>
      ))}
    </Box>
  );
};

export default Accordion;
